/**
 * Pure decision helpers for global publish-status polling.
 *
 * The publish lock is NOT the source of truth for whether a publish is still
 * running. A run can remain non-terminal after the lock was released early (e.g.
 * a coordinated sibling-feed pre-pipeline failure) or after the lock TTL expired.
 * These helpers let `App.vue` keep polling until the visible run reaches a
 * terminal state, and keep the logic unit-testable in isolation.
 */

export type PipelineStatus =
  | { isRunning: boolean; status: string; statusText: string }
  | null

export interface ProcessingSignal {
  isProcessing: boolean
  processingStatus: string
}

export const TERMINAL_PUBLISH_STATUSES = new Set([
  'SUCCEEDED',
  'FAILED',
  'SKIPPED',
  'FLAGGED_LARGE_DELTA',
])
export const NON_TERMINAL_PUBLISH_STATUSES = new Set(['INITIATED', 'PROCESSING'])

export function isTerminalPublishStatus(status: string | null | undefined): boolean {
  return TERMINAL_PUBLISH_STATUSES.has(String(status || '').toUpperCase())
}

export function isNonTerminalPublishStatus(status: string | null | undefined): boolean {
  return NON_TERMINAL_PUBLISH_STATUSES.has(String(status || '').toUpperCase())
}

export const isTerminalStatus = isTerminalPublishStatus
export const isRunningStatus = isNonTerminalPublishStatus

export function shouldClearActivePublishForUnlockedStatus(
  status: string | null | undefined,
): boolean {
  return !isNonTerminalPublishStatus(status)
}

/**
 * Derive the global processing signal from a recent-jobs poll when NO publish
 * lock is held. `checkPipelineStatus` reports the LATEST job only (with no active
 * lock there is no in-flight publish that an older non-terminal job could belong
 * to), so a stale orphaned INITIATED/PROCESSING job cannot keep the banner alive
 * once a newer job is terminal.
 */
export function deriveProcessingFromPipelineStatus(pipeline: PipelineStatus): ProcessingSignal {
  if (!pipeline) return { isProcessing: false, processingStatus: '' }
  const s = String(pipeline.status || '').toUpperCase()
  const running = pipeline.isRunning === true || isNonTerminalPublishStatus(s)
  return {
    isProcessing: running,
    processingStatus: isTerminalPublishStatus(s) || running ? s : '',
  }
}

/**
 * Whether the global 30-second poller should keep running. Lock status is
 * intentionally NOT a condition here — it gates only the Publish button.
 */
export function shouldKeepPollingProcessing(params: {
  isProcessing: boolean
  hasPendingPublish: boolean
  hasPendingRepublish?: boolean
  routePath: string
}): boolean {
  return (
    params.isProcessing === true ||
    params.hasPendingPublish === true ||
    params.hasPendingRepublish === true ||
    params.routePath === '/unpublished'
  )
}

/**
 * Whether a no-lock SUCCEEDED observation should reconcile/clear pending-publish
 * state. Never reconcile while any run is still in-flight, so a newer SUCCEEDED
 * cannot clear pending state for an older PROCESSING run.
 */
export function shouldReconcileOnSuccess(params: {
  isRunning: boolean
  status: string | null | undefined
  hasPendingPublish: boolean
  publishInitiatedAt?: string | null
}): boolean {
  return (
    params.isRunning === false &&
    String(params.status || '').toUpperCase() === 'SUCCEEDED' &&
    (params.hasPendingPublish === true || !!params.publishInitiatedAt)
  )
}
